import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../services/authService';
import { supabase } from '../services/supabaseClient';

const RESET_REDIRECT_URL =
  window.location.hostname === 'localhost'
    ? 'http://localhost:3000/reset-password'
    : 'https://app.smartgage.com.mx/reset-password';

export default function Login({ onLogin }) {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mostrarPassword, setMostrarPassword] = useState(false);

  const [errorMsg, setErrorMsg] = useState('');
  const [mensaje, setMensaje] = useState('');

  const [iniciandoSesion, setIniciandoSesion] = useState(false);
  const [enviandoRecuperacion, setEnviandoRecuperacion] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (iniciandoSesion) return;

    setErrorMsg('');
    setMensaje('');

    if (!email.trim() || !password) {
      setErrorMsg('Ingresa tu correo y contraseña.');
      return;
    }

    setIniciandoSesion(true);

    try {
      const { user, error } = await login(email.trim(), password);

      if (error) {
        console.error('❌ Login error:', error);

        setErrorMsg(
          typeof error === 'string'
            ? error
            : error?.message || 'No se pudo iniciar sesión.'
        );
        return;
      }

      if (!user) {
        setErrorMsg('No se pudo cargar la información del usuario.');
        return;
      }

      if (onLogin) {
        onLogin(user);
      }

      navigate('/', { replace: true });
    } catch (error) {
      console.error('❌ Login error:', error);

      setErrorMsg(
        'No se pudo iniciar sesión. Verifica tus datos e inténtalo nuevamente.'
      );
    } finally {
      setIniciandoSesion(false);
    }
  };

  const handleRecuperarPassword = async () => {
    if (enviandoRecuperacion) return;

    setErrorMsg('');
    setMensaje('');

    const correo = email.trim();

    if (!correo) {
      setErrorMsg(
        'Ingresa tu correo para enviarte las instrucciones de recuperación.'
      );
      return;
    }

    setEnviandoRecuperacion(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(correo, {
        redirectTo: RESET_REDIRECT_URL,
      });

      if (error) {
        throw error;
      }

      setMensaje(
        'Te enviamos un correo con las instrucciones para restablecer tu contraseña.'
      );
    } catch (error) {
      console.error('❌ Error recuperando contraseña:', error);

      setErrorMsg(
        'No se pudo enviar el correo de recuperación. Inténtalo nuevamente.'
      );
    } finally {
      setEnviandoRecuperacion(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="px-6 pb-5 pt-7 sm:px-8">
          <div className="mb-7 text-center">
            <div className="flex justify-center">
              <img
                src="/logo-smartgage-light.png"
                alt="SmartGage - Metrology Management"
                className="h-auto w-full max-w-[280px] object-contain"
              />
            </div>

            <p className="mt-3 text-sm text-slate-500">
              Sistema de Gestión de Instrumentos
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="login-email"
                className="mb-2 block text-sm font-medium text-slate-700"
              >
                Correo
              </label>

              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-base text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
              />
            </div>

            <div>
              <label
                htmlFor="login-password"
                className="mb-2 block text-sm font-medium text-slate-700"
              >
                Contraseña
              </label>

              <div className="relative">
                <input
                  id="login-password"
                  type={mostrarPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 pr-20 text-base text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                />

                <button
                  type="button"
                  onClick={() => setMostrarPassword((valor) => !valor)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-sm font-medium text-slate-500 transition hover:text-cyan-700"
                  aria-label={
                    mostrarPassword
                      ? 'Ocultar contraseña'
                      : 'Mostrar contraseña'
                  }
                  aria-pressed={mostrarPassword}
                >
                  {mostrarPassword ? 'Ocultar' : 'Ver'}
                </button>
              </div>
            </div>

            {errorMsg && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {errorMsg}
              </div>
            )}

            {mensaje && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
                {mensaje}
              </div>
            )}

            <button
              type="submit"
              disabled={iniciandoSesion}
              className={`h-11 w-full rounded-lg text-sm font-semibold text-white transition ${
                iniciandoSesion
                  ? 'cursor-not-allowed bg-slate-400'
                  : 'bg-cyan-600 hover:bg-cyan-700'
              }`}
            >
              {iniciandoSesion ? 'Iniciando sesión...' : 'Iniciar sesión'}
            </button>
          </form>

          <div className="mt-4">
            <button
              type="button"
              onClick={handleRecuperarPassword}
              disabled={enviandoRecuperacion}
              className="text-sm font-medium text-cyan-700 transition hover:text-cyan-800 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {enviandoRecuperacion
                ? 'Enviando instrucciones...'
                : '¿Olvidaste tu contraseña?'}
            </button>
          </div>
        </div>

        <div className="border-t border-slate-100 bg-slate-50 px-6 py-4 text-center sm:px-8">
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} SmartGage. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
}